import { otherSeat } from '@xox/shared'
import { LogOut, RotateCcw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useAutoSeat } from '../../hooks/useAutoSeat'
import { useConnectRoom } from '../../hooks/useConnectRoom'
import { Confetti } from '../../ice/art/Confetti'
import { BOSS_THEME } from '../../lib/bossTheme'
import type { SnapshotOf } from '../../store/duel'
import { useSession } from '../../store/session'
import { BoardOverlay } from '../BoardOverlay'
import { CpuRecordCard } from '../CpuRecordCard'
import { InvitePanel } from '../InvitePanel'
import { ReactionBar, ReactionOverlay } from '../Reactions'
import { isRoomGone, JoiningNotice, RoomChrome, RoomClosedCard } from '../RoomShell'
import { SeatCard } from '../SeatCard'
import { TurnBar } from '../TurnBar'
import { Button, Card, LinkButton } from '../ui'
import type { DuelPageProps, DuelSettingsBase } from './defs'

/** A duel's room: seats, clock, the game's board, results, reactions and the side panel. */
export function DuelRoom<Snap extends SnapshotOf<S>, S extends DuelSettingsBase>({
  def,
}: DuelPageProps<Snap, S>) {
  const { code = '' } = useParams()
  const navigate = useNavigate()
  const me = useSession((s) => s.me)
  const room = def.store()
  // The result card hidden so the finished board can be reviewed; a new game brings it back.
  const [reviewing, setReviewing] = useState(false)
  useConnectRoom(code, me?.playerId, def.store)
  useAutoSeat(room.snapshot !== null, room.role !== 'spectator', room.takeSeat)
  const finished = room.snapshot ? room.snapshot.game.status !== 'playing' : false
  useEffect(() => {
    if (!finished) setReviewing(false)
  }, [finished])

  const snap = room.snapshot
  const role = room.role

  if (me && isRoomGone(room)) {
    return (
      <RoomClosedCard
        closedReason={room.closedReason === 'closed' ? 'closed' : 'expired'}
        noun="room"
        onBack={() => navigate(def.home)}
        backLabel="Back home"
      />
    )
  }
  if (!snap) return <JoiningNotice label={`Joining room ${code}…`} />

  const game = snap.game
  const seated = role !== 'spectator'
  const bothSeated = snap.seats.X !== null && snap.seats.O !== null
  const myTurn = seated && game.turn === role && game.status === 'playing' && bothSeated
  const canPlay = myTurn && room.status === 'open'
  const openSeat = !bothSeated
  const isHost = snap.hostId === room.playerId
  const cpuMode = snap.mode === 'cpu'
  const draw = game.status === 'draw'
  const victory = finished && cpuMode && !draw && seated && game.winner === role
  const theme = BOSS_THEME[def.id]
  const boardNote = def.boardNote?.(snap, role) ?? null
  const turnHint = def.turnHint?.(snap) ?? null
  const nameOf = (seat: 'X' | 'O') => snap.seats[seat]?.nickname ?? seat

  const leave = () => {
    room.leave()
    navigate(def.home)
  }

  const resultText = !finished
    ? null
    : draw
      ? cpuMode && seated
        ? def.copy.drawResult
        : 'A draw'
      : seated
        ? game.winner === role
          ? 'You win!'
          : cpuMode
            ? `${def.bot.nickname} wins. Again.`
            : 'You lose'
        : `${game.winner ? nameOf(game.winner) : '?'} wins`
  // Rematch state shared by the result card and the review bar.
  const waitingForOther = !cpuMode && seated && snap.rematch[role]
  const canRematch = seated && (cpuMode || bothSeated) && !waitingForOther
  const reasonText = draw
    ? def.copy.drawReason
    : game.status === 'forfeit'
      ? `${nameOf(otherSeat(game.winner ?? 'X'))} ran out of time or left`
      : game.status === 'won'
        ? def.copy.lineWin
        : null

  return (
    <div className="flex flex-1 flex-col gap-4 lg:flex-row lg:items-start lg:gap-8">
      <RoomChrome error={room.error} onDismiss={room.dismissError} status={room.status} />

      <section className="flex flex-1 flex-col items-center gap-3" aria-label="Game">
        <div className="flex w-full max-w-[560px] items-stretch gap-2">
          {(['X', 'O'] as const).map((seat, i) => (
            <div key={seat} className="contents">
              {i === 1 && (
                <div className="flex shrink-0 flex-col items-center justify-center px-1 tabular-nums">
                  <div className="text-2xl font-black leading-none">
                    {snap.series.X}
                    <span className="mx-1 text-slate-400">:</span>
                    {snap.series.O}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">series</div>
                </div>
              )}
              <SeatCard
                mark={seat}
                style={def.seatStyle}
                player={snap.seats[seat]}
                isYou={role === seat}
                online={snap.presence[seat]}
                active={!finished && bothSeated && game.turn === seat}
                ready={snap.rematch[seat]}
                canTake={!seated && snap.seats[seat] === null}
                onTake={room.takeSeat}
              />
            </div>
          ))}
        </div>

        <TurnBar
          deadlineAt={finished ? null : snap.deadlineAt}
          turnMs={snap.settings.turnSeconds * 1000}
          turn={game.turn}
          mine={myTurn}
          name={nameOf(game.turn)}
          tone={def.seatStyle === 'disc' ? 'neutral' : undefined}
        />

        {boardNote && <p className="text-center text-sm text-slate-500">{boardNote}</p>}
        <div
          className={`relative ${finished && cpuMode && !draw && !victory ? 'animate-shake' : ''}`}
        >
          <def.Board
            snap={snap}
            role={role}
            canPlay={canPlay}
            pending={room.pending}
            onMove={room.move}
          />
          {finished && cpuMode && !reviewing && (
            <BoardOverlay onPeek={() => setReviewing(true)}>
              {victory && <Confetti />}
              <div
                className={`text-xs font-black uppercase tracking-[0.25em] ${draw ? theme.accent : victory ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}
              >
                {draw ? 'Draw' : victory ? 'Victory' : seated ? 'Defeat' : 'Game over'}
              </div>
              <div className="mt-1 text-2xl font-black leading-tight">{resultText}</div>
              {reasonText && <div className="mt-1 text-sm text-slate-500">{reasonText}</div>}
              <p className="mt-2 text-xs tabular-nums text-slate-500">
                Series {snap.series.O} – {snap.series.X}
              </p>
              {seated && (
                <Button
                  variant="arcade"
                  tone={theme.arcade}
                  className="mt-4 w-full"
                  onClick={room.rematch}
                >
                  <RotateCcw className="size-5" aria-hidden /> Play again
                </Button>
              )}
              {!seated && <p className="mt-3 text-sm text-slate-500">Waiting for the next game…</p>}
            </BoardOverlay>
          )}
          {finished && !cpuMode && !reviewing && (
            <BoardOverlay onPeek={() => setReviewing(true)}>
              <div className="text-2xl font-black">{resultText}</div>
              {reasonText && <div className="mt-1 text-sm text-slate-500">{reasonText}</div>}
              {seated && bothSeated && (
                <Button className="mt-4 w-full" onClick={room.rematch} disabled={waitingForOther}>
                  <RotateCcw className="size-4" aria-hidden />
                  {waitingForOther
                    ? `Waiting for ${nameOf(otherSeat(role))}…`
                    : snap.rematch[otherSeat(role)]
                      ? 'Accept rematch'
                      : 'Rematch'}
                </Button>
              )}
              {seated && !bothSeated && (
                <p className="mt-3 text-sm text-slate-500">
                  Waiting for someone to take the open seat.
                </p>
              )}
              {!seated && <p className="mt-3 text-sm text-slate-500">Waiting for a rematch…</p>}
            </BoardOverlay>
          )}
          <ReactionOverlay reactions={room.reactions} />
        </div>

        {finished && reviewing && (
          <div
            className="flex w-full max-w-[560px] flex-col gap-2 rounded-2xl bg-white p-3 shadow-lg ring-1 ring-slate-200 sm:flex-row sm:items-center dark:bg-slate-900 dark:ring-slate-700"
            role="status"
          >
            <div className="min-w-0 flex-1 text-center sm:text-left">
              <div className="font-black leading-tight">{resultText}</div>
              {reasonText && <div className="text-xs text-slate-500">{reasonText}</div>}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setReviewing(false)}>
                Result
              </Button>
              {canRematch && (
                <Button
                  variant={cpuMode ? 'arcade' : 'primary'}
                  tone={theme.arcade}
                  className={`flex-1 ${cpuMode ? 'min-h-11 px-4 py-2 text-sm' : ''}`}
                  onClick={room.rematch}
                >
                  <RotateCcw className="size-4" aria-hidden /> Play again
                </Button>
              )}
            </div>
          </div>
        )}

        <p className="text-sm text-slate-500" aria-live="polite">
          {!bothSeated
            ? 'Waiting for an opponent…'
            : finished
              ? `${nameOf(snap.nextFirst)} starts the next game`
              : role === 'spectator'
                ? `${nameOf(game.turn)} to move`
                : myTurn
                  ? (turnHint ?? 'Your turn')
                  : `${nameOf(game.turn)} is thinking…`}
        </p>

        <ReactionBar onReact={room.react} set={def.reactionSet} />
      </section>

      <aside className="flex w-full flex-col gap-3 lg:w-72 lg:shrink-0" aria-label="Room actions">
        {openSeat && (
          <Card className="flex flex-col gap-3">
            <h2 className="font-semibold">{seated ? 'Invite a friend' : 'Join this game'}</h2>
            {seated ? (
              <InvitePanel code={code} path={def.roomPath} game={def.id} />
            ) : (
              <Button onClick={room.takeSeat}>Take the open seat</Button>
            )}
          </Card>
        )}
        {!openSeat && cpuMode && (
          <CpuRecordCard
            // Remount after every finished game so the record refreshes.
            key={snap.series.X + snap.series.O + (draw ? 1 : 0)}
            bot={def.bot}
            load={def.api.cpuStatsSoon}
            playerId={room.playerId}
            spectators={snap.spectators}
          />
        )}
        {!openSeat && !cpuMode && (
          <Card className="flex flex-col gap-3">
            <h2 className="font-semibold">Share this room</h2>
            <InvitePanel code={code} path={def.roomPath} game={def.id} compact />
            <p className="text-xs text-slate-500">
              {snap.spectators > 0
                ? `${snap.spectators} watching`
                : 'Anyone with the link can watch live.'}
            </p>
          </Card>
        )}
        <p className="px-1 text-xs text-slate-500 dark:text-slate-400">
          Rules: {def.settings.describe(snap.settings)}.
        </p>
        {!seated && (
          <div className="rounded-xl bg-slate-100 px-3 py-2 text-center text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            You are watching
          </div>
        )}
        <div className="flex gap-2">
          {seated ? (
            <Button variant="ghost" className="flex-1" onClick={leave}>
              <LogOut className="size-4" aria-hidden />{' '}
              {cpuMode ? 'Give up' : isHost ? 'End room for everyone' : 'Leave seat'}
            </Button>
          ) : (
            <LinkButton to="/" className="flex-1">
              Back home
            </LinkButton>
          )}
        </div>
      </aside>
    </div>
  )
}
